import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import _ from 'lodash';
import './Analytcs.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  useAnalyticsData,
  useAnalyticsInfo,
  useRunIngestionAnalytics,
} from '../../../hooks/ingestions/useIngestions';
import {t} from 'i18next';
import {roleColumnVisibilityMap} from './analyticsUtils';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import IngetionTabPanelHeader from '../component/TabPanelHeader/IngetionTabPanelHeader';
import IngestionRadioOptions from '../component/IngestionRadioOptions/IngestionRadioOptions';
import {
  AnalyticsRunOptionType,
  ToastMessageConfigType,
} from '../../../api/types/ingestionApi.type';
import CellWrapper from '../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';

const defaultSortColumn = {
  field: 'startTime',
  sort: 'desc',
};

const PAGE_SIZE_DEFAULT = 10;

const Analytics: React.FC = () => {
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_DEFAULT);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    defaultSortColumn,
  ] as GridSortModel);

  const [selectedMode, setSelectedMode] = useState<string>('');
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const {mutate: runAnalytics, isPending: isRunAnalyticsLoading} =
    useRunIngestionAnalytics();
  const analyticsInfoResult = useAnalyticsInfo() ?? {};
  const analyticsInfo = analyticsInfoResult.data ?? {
    ingestionCount: 0,
    canRun: false,
    isCalEnabled: false,
    canFullAnalytics: 0,
    canAnalyticsWithExistingClusters: 0,
    canCalFullReIndex: 0,
  };

  // Build payload from sortModel + pagination
  const payload = useMemo(
    () => ({
      pageNumber,
      pageSize,
      sortingColumn: sortModel[0]?.field ?? defaultSortColumn.field,
      direction: sortModel[0]?.sort ?? defaultSortColumn.sort,
    }),
    [pageNumber, pageSize, sortModel]
  );

  // Unified hook call for analytics data
  const {
    data,
    isLoading,
    refetch: refetchAnalytics,
  } = useAnalyticsData(payload);

  const analytics = data ?? {data: [], totalRecords: 0};
  const isAnalyticsLoading = isLoading ?? false;

  const onPageChangeHandler = (value: number) => setPageNumber(value);

  const hiddenCols = roleColumnVisibilityMap[roleId || 0] ?? [];
  const analyticsColumns = useMemo<GridColDef[]>(() => {
    return [
      {field: 'runType', headerName: t('ingestions.common.runType'), flex: 1},
      {
        field: 'analyticsType',
        headerName: t('ingestions.common.analyticsType'),
        flex: 1,
      },
      {
        field: 'startTime',
        headerName: t('ingestions.common.startTime'),
        flex: 1,
        headerClassName: 'typo-text label2_size',
      },
      {
        field: 'endTime',
        headerName: t('ingestions.common.endTime'),
        flex: 1,
        headerClassName: 'typo-text label2_size',
      },
      {field: 'status', headerName: t('ingestions.common.status'), flex: 1},
      {
        field: 'errorMessage',
        headerName: t('ingestions.common.errorMessage'),
        headerClassName: 'last-column',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <CellWrapper
            toolTipContent={params.value}
            cellParams={params}
            dataTestId={`analytics-error-cell-wrapper-${params.id}`}
          >
            <span
              // title={params.row.errorMessage}
              className="error-message-caption"
              data-testid={`analytics-span-error-${params.id}`}
            >
              {_.isEmpty(params.row.errorMessage)
                ? '--'
                : params.row.errorMessage}
            </span>
          </CellWrapper>
        ),
      },
    ];
  }, [t]);

  const visibleColumns = analyticsColumns.filter(
    (col) => !hiddenCols.includes(col.field)
  );

  const toggleOptions = useMemo(() => {
    const analytics = t(
      'ingestions.ingestionAnalytics.toggle.options.analytics',
      {
        returnObjects: true,
      }
    ) as {
      label: string;
      toolTip: string;
    };

    const incrementalAnalytics = t(
      'ingestions.ingestionAnalytics.toggle.options.icrementalAnalytics',
      {
        returnObjects: true,
      }
    ) as {
      label: string;
      toolTip: string;
    };
    return [
      {
        label: analytics.label,
        tooltip: analytics.toolTip,
        value: 'ANALYTICS_WII',
        disabeld: !analyticsInfo.canRun || analyticsInfo.canFullAnalytics !== 1,
      },
      {
        label: incrementalAnalytics.label,
        tooltip: incrementalAnalytics.toolTip,
        value: 'ANALYTICS_WEC_WII',
        disabeld:
          !analyticsInfo.canRun ||
          analyticsInfo.canAnalyticsWithExistingClusters !== 1,
      },
    ];
  }, [analyticsInfo, t]);

  const runIngestionsAnalytics = (value: string) => {
    setSelectedMode(value);
    runAnalytics(
      {selectedValue: value as AnalyticsRunOptionType},
      {
        onSuccess: () => {
          setSelectedMode('');
          showToast({
            message: t(
              'ingestions.ingestionAnalytics.toastMsg.runStartedSuccessMsg'
            ),
            type: ToastType.Success,
          });
          setPageNumber(1);
          setTimeout(() => {
            refetchAnalytics();
          }, 250);
          analyticsInfoResult?.refetch?.();
        },
        onError: () => {
          showToast({
            message: t('ingestions.ingestionAnalytics.toastMsg.rerunFailed'),
            type: ToastType.Error,
          });
        },
      }
    );
  };

  const dataGridClass = useMemo(() => {
    if (isAnalyticsLoading) return 'index-grid-loading';

    if ((analytics?.totalRecords ?? 0) === 0)
      return 'index-grid-no-row-overlay';

    return 'index-grid';
  }, [isAnalyticsLoading, analytics?.totalRecords]);

  useEffect(() => {
    refetchAnalytics();
  }, [sortModel, pageNumber, pageSize]);

  const renderTopHeader = () => {
    return (
      <Box
        className="analytics-top-header"
        display="flex"
        flexDirection="column"
      >
        <IngetionTabPanelHeader
          title={t('ingestions.ingestionAnalytics.subHeading')}
          subTitle={t('ingestions.ingestionAnalytics.newIngestions', {
            number: analyticsInfo.ingestionCount ?? 0,
          })}
        />

        {/** Reindex project options */}
        <IngestionRadioOptions
          label={t('ingestions.ingestionAnalytics.toggle.toggleLabel')}
          options={toggleOptions}
          canRun={analyticsInfo.canRun}
          onRun={runIngestionsAnalytics}
          defaultValue={selectedMode}
          runLoading={isRunAnalyticsLoading}
        />
      </Box>
    );
  };

  const showToast = useCallback((config: ToastMessageConfigType) => {
    setToastMsgConfig({
      show: true,
      title: config.title,
      message: config.message,
      type: config.type,
    });
  }, []);

  return (
    <Box className="analytics-container">
      {renderTopHeader()}
      <Box data-testid="users-table" className="analytics-table-container">
        <ArrDataGrid
          rows={analytics?.data ?? []}
          columns={visibleColumns}
          rowCount={analytics.totalRecords}
          currentPage={pageNumber}
          itemsPerPage={pageSize}
          setItemsPerPage={setPageSize}
          paginationMode="server"
          initialState={{
            sorting: {
              sortModel: [defaultSortColumn] as GridSortModel,
            },
          }}
          onPageChange={onPageChangeHandler}
          entityType={t('ingestions.common.entries')}
          primaryKey="analyticsHistoryId"
          className={dataGridClass}
          onSortModelChange={setSortModel}
          sortModel={sortModel}
          loading={isAnalyticsLoading}
          showPerPageOptions
          pageSizeOptions={[10]}
          hideFooter={(analytics?.totalRecords ?? 0) === 0}
          alwaysShowPagination
          noRowsOverlayCustomText={t(
            'ingestions.ingestionAnalytics.noDataOverlay'
          )}
        />
      </Box>
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
    </Box>
  );
};

export default Analytics;
